import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Layout from '../../components/ui/Layout';
import { SITE_URL, SITE_NAME, getProductShareUrl, getProductShareSlug } from '../../constants/site';
import ShareButton from '../../components/ui/ShareButton';
import { db } from '../../firebase';
import { collection, getDocs, query, where, orderBy, limit, startAfter, Timestamp } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { Form, Button, Container, Row, Col, Card, Badge, Alert, Modal, ListGroup } from 'react-bootstrap';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import PublicProductsDashboard from '../../components/public/PublicProductsDashboard';
import { createLogger } from '../../utils/logger';
import { effectiveDisplayPrice, totalDealsCountForInventory, typePriceFromMap } from '../../utils/inventoryPricing';

const logger = createLogger('PublicProducts');

/** Firestore created_at can be Timestamp, Date, or number (ms). */
function createdAtMillis(value) {
  if (value == null) return 0;
  if (typeof value === 'number') return value;
  if (value instanceof Timestamp) return value.toMillis();
  if (value instanceof Date) return value.getTime();
  if (typeof value.toMillis === 'function') return value.toMillis();
  return 0;
}

// Product Image Component
const ProductImage = ({ imagePath, altText }) => {
  const [imageUrl, setImageUrl] = useState('');
  const storage = getStorage();
  
  useEffect(() => {
    if (imagePath && typeof imagePath === 'string' && !imagePath.startsWith('blob:')) {
      const imageRef = ref(storage, imagePath);
      getDownloadURL(imageRef)
        .then(setImageUrl)
        .catch(err => logger.error("[ProductImage] Error fetching image URL for path:", imagePath, err));
    } else if (imagePath && typeof imagePath === 'string' && imagePath.startsWith('blob:')) {
        logger.warn("[ProductImage] Attempted to use a blob URL with Firebase Storage ref. Path:", imagePath);
        setImageUrl('');
    } else if (imagePath) {
        logger.warn("[ProductImage] imagePath is not a string or is invalid:", imagePath);
        setImageUrl('');
    }
  }, [imagePath, storage]);

  if (imagePath && typeof imagePath === 'string' && imagePath.startsWith('blob:') && !imageUrl) {
    return <div className="card-img-top" style={{height: '180px', backgroundColor: '#f0f0f0', display:'flex', alignItems:'center', justifyContent:'center'}}><span>Blob Image (Local Preview)</span></div>;
  } 
  if (!imageUrl) return <div className="card-img-top" style={{height: '180px', backgroundColor: '#f0f0f0', display:'flex', alignItems:'center', justifyContent:'center'}}><span>Loading Image...</span></div>;
  return <img src={imageUrl} className="card-img-top" alt={altText} style={{height: '180px', objectFit: 'cover'}}/>;
};

export default function PublicProducts() {
  const [products, setProducts] = useState([]);
  const [supermarkets, setSupermarkets] = useState({});
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState('');
  const [expandedProducts, setExpandedProducts] = useState(new Set());
  const [deliveryFilter, setDeliveryFilter] = useState(false);
  const [inventoryDetails, setInventoryDetails] = useState({}); // { [productId_supermarketId]: inventoryData }
  const [showDealsModal, setShowDealsModal] = useState(false);
  const [selectedProductDeals, setSelectedProductDeals] = useState([]);
  const [selectedProductName, setSelectedProductName] = useState('');

  const navigate = useNavigate();
  const location = useLocation();
  const PRODUCTS_PER_PAGE = 20;

  // Apply category filter from dashboard link (e.g. clicking a top category)
  useEffect(() => {
    if (location.state?.categoryFilter) {
      setCategoryFilter(location.state.categoryFilter);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.categoryFilter, location.pathname, navigate]);

  const breadcrumbItems = [
    { path: "/", label: "Home" },
    { label: "Products" }
  ];



  // Function to fetch inventory details for deals
  const fetchInventoryDetails = async (productId, supermarketId) => {
    const key = `${productId}_${ supermarketId }`;
    if (inventoryDetails[key]) return; // Already fetched
    
    try {
      const inventoryRef = collection(db, 'Inventory');
      const inventoryQuery = query(
        inventoryRef,
        where('product_id', '==', productId),
        where('supermarket_id', '==', supermarketId),
        limit(1)
      );
      
      const inventorySnapshot = await getDocs(inventoryQuery);
      if (!inventorySnapshot.empty) {
        const inventoryData = inventorySnapshot.docs[0].data();
        setInventoryDetails(prev => ({ ...prev, [key]: inventoryData }));
      }
    } catch (error) {
      logger.error(`Failed to fetch inventory details for ${productId}:`, error);
    }
  };

  // Function to handle viewing deals from all hardware stores
  const handleViewDeals = (product) => {
    const { id: productId, display_name: productName, supermarketPricing, product_types: productTypes = [] } = product;
    const typeLabel = (typeId) =>
      (productTypes || []).find((t) => t.id != null && String(t.id) === String(typeId))?.name || typeId;

    const allDeals = [];

    Object.keys(supermarketPricing || {}).forEach((supermarketId) => {
      const inventoryKey = `${productId}_${supermarketId}`;
      const inventoryData = inventoryDetails[inventoryKey];
      const pricing = supermarketPricing[supermarketId];
      if (!inventoryData) return;

      const pushDeal = (deal, { regularPrice, productTypeName }) => {
        allDeals.push({
          ...deal,
          supermarketId,
          supermarketName: pricing.supermarketName,
          regularPrice,
          deliveryEnabled: pricing.deliveryEnabled,
          productTypeName: productTypeName || undefined,
        });
      };

      const tdd = inventoryData.type_discount_deals;
      if (tdd && typeof tdd === 'object' && Object.keys(tdd).length > 0) {
        Object.entries(tdd).forEach(([typeId, deals]) => {
          if (!Array.isArray(deals)) return;
          const rp =
            typePriceFromMap(inventoryData.type_prices, typeId) ??
            (pricing.sale_price != null ? Number(pricing.sale_price) : null);
          deals.forEach((deal) => pushDeal(deal, { regularPrice: rp, productTypeName: typeLabel(typeId) }));
        });
      } else if (inventoryData.discount_deals && inventoryData.discount_deals.length > 0) {
        inventoryData.discount_deals.forEach((deal) =>
          pushDeal(deal, { regularPrice: pricing.sale_price, productTypeName: null })
        );
      }
    });

    if (allDeals.length > 0) {
      // Sort deals by quantity (ascending) then by price (ascending)
      allDeals.sort((a, b) => {
        if (a.quantity !== b.quantity) {
          return a.quantity - b.quantity;
        }
        return a.price - b.price;
      });
      
      setSelectedProductDeals(allDeals);
      setSelectedProductName(productName);
      setShowDealsModal(true);
    }
  };

  // Function to close deals modal
  const handleCloseDealsModal = () => {
    setShowDealsModal(false);
    setSelectedProductDeals([]);
    setSelectedProductName('');
  };

  // Fetch all supermarkets for reference
  useEffect(() => {
    const fetchSupermarkets = async () => {
      try {
        const supermarketsSnapshot = await getDocs(collection(db, 'Supermarkets'));
        const supermarketsData = {};
        supermarketsSnapshot.docs.forEach(doc => {
          supermarketsData[doc.id] = { id: doc.id, ...doc.data() };
        });
        setSupermarkets(supermarketsData);
      } catch (error) {
        logger.error("Error fetching supermarkets:", error);
      }
    };
    fetchSupermarkets();
  }, []);

  // Fetch initial products
  const fetchInitialProducts = useCallback(async (currentSupermarkets = {}) => {
    setLoading(true);
    setProducts([]);
    setError('');

    try {
      // Build products query
      let productsQuery;
      
      if (categoryFilter) {
        // Composite index required for category + orderBy(created_at): deploy firestore.indexes.json
        // (Products: category ASC, created_at DESC). If index is missing, getDocs throws failed-precondition.
        productsQuery = query(
          collection(db, 'Products'),
          where('category', '==', categoryFilter),
          orderBy('created_at', 'desc'),
          limit(PRODUCTS_PER_PAGE)
        );
      } else {
        productsQuery = query(
          collection(db, 'Products'), 
          orderBy('created_at', 'desc'),
          limit(PRODUCTS_PER_PAGE)
        );
      }

      let productsSnapshot;
      let filteredFetchedProducts;

      try {
        productsSnapshot = await getDocs(productsQuery);
        filteredFetchedProducts = productsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (fetchErr) {
        // Missing composite index: equality on category + orderBy(created_at) needs an index.
        const isIndexError =
          fetchErr?.code === 'failed-precondition' ||
          (typeof fetchErr?.message === 'string' && fetchErr.message.toLowerCase().includes('index'));
        if (categoryFilter && isIndexError) {
          logger.warn('Category ordered query failed (index); falling back to category-only query + client sort', fetchErr);
          const fallbackQuery = query(
            collection(db, 'Products'),
            where('category', '==', categoryFilter)
          );
          productsSnapshot = await getDocs(fallbackQuery);
          filteredFetchedProducts = productsSnapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .sort((a, b) => createdAtMillis(b.created_at) - createdAtMillis(a.created_at))
            .slice(0, PRODUCTS_PER_PAGE);
        } else {
          throw fetchErr;
        }
      }

      // Use passed supermarkets or current state
      const useSupermarkets = Object.keys(currentSupermarkets).length > 0 ? currentSupermarkets : supermarkets;


      // For each product, fetch inventory data from all hardware stores
      const productsWithInventory = await Promise.all(
        filteredFetchedProducts.map(async (product) => {
          try {
            const inventoryQuery = query(
              collection(db, 'Inventory'), 
              where('product_id', '==', product.id),
              where('quantity', '>', 0)
            );
            const inventorySnapshot = await getDocs(inventoryQuery);
            
            const supermarketPricing = {};
            const inventoryPromises = [];
            
            inventorySnapshot.docs.forEach(doc => {
              const inventoryData = doc.data();
              const supermarketId = inventoryData.supermarket_id;
              
              supermarketPricing[supermarketId] = {
                sale_price: effectiveDisplayPrice(inventoryData) ?? inventoryData.sale_price,
                quantity: inventoryData.quantity,
                supermarketName: useSupermarkets[supermarketId]?.display_name || 'Unknown',
                deliveryEnabled: useSupermarkets[supermarketId]?.delivery_enabled || false
              };
              
              // Fetch inventory details for deals
              inventoryPromises.push(fetchInventoryDetails(product.id, supermarketId));
            });

            // Wait for all inventory details to be fetched
            await Promise.all(inventoryPromises);

            return {
              ...product,
              supermarketPricing
            };
          } catch (error) {
            logger.error(`Error fetching inventory for product ${product.id}:`, error);
            return {
              ...product,
              supermarketPricing: {}
            };
          }
        })
      );

      // Filter out products that have no supermarket pricing (not available at any supermarket)
      const availableProducts = productsWithInventory.filter(product => 
        Object.keys(product.supermarketPricing).length > 0
      );




      // Set pagination info after filtering
      if (productsSnapshot.docs.length > 0) {
        // setLastDoc(productsSnapshot.docs[productsSnapshot.docs.length - 1]); // Removed
      }
      // Pagination: full page from server, or fallback filled up to page size
      setHasMore(filteredFetchedProducts.length === PRODUCTS_PER_PAGE);

      setProducts(availableProducts);

    } catch (error) {
      logger.error("Error fetching products:", error);
      
      if (
        error?.code === 'failed-precondition' ||
        (typeof error?.message === 'string' && error.message.toLowerCase().includes('index'))
      ) {
        const linkMatch =
          typeof error.message === 'string' ? error.message.match(/https:\/\/[^\s)]+/) : null;
        setError(
          linkMatch
            ? `Database index required for category filtering. Create the index (opens from console link in the browser developer tools), or run: firebase deploy --only firestore:indexes`
            : 'Database index required for category filtering. Run: firebase deploy --only firestore:indexes (from the project root), then try again.'
        );
      } else {
        setError('Failed to load products. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryFilter]); // supermarkets intentionally excluded to prevent infinite loop

  // Load more products (for infinite scroll)
  const loadMoreProducts = useCallback(async () => {
    if (loading || !hasMore || !supermarkets || Object.keys(supermarkets).length === 0) return;
    
    setLoading(true);
    try {
      const lastProduct = products[products.length - 1];
      const lastProductId = lastProduct ? lastProduct.id : null;
      
      if (!lastProductId) {
        setHasMore(false);
        setLoading(false);
        return;
      }

      const productsQuery = query(
        collection(db, 'Products'),
        where('status', '==', 'active'),
        orderBy('display_name'),
        startAfter(lastProductId),
        limit(10)
      );

      const snapshot = await getDocs(productsQuery);
      const fetchedProducts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Filter products that are available at any of the current supermarkets
      const availableProducts = fetchedProducts.filter(product => {
        return Object.values(supermarkets).some(supermarket => 
          supermarket.products && supermarket.products.includes(product.id)
        );
      });

      if (availableProducts.length > 0) {
        setProducts(prev => [...prev, ...availableProducts]);
      }

      setHasMore(fetchedProducts.length === 10);
    } catch (error) {
      logger.error('Error loading more products:', error);
    } finally {
      setLoading(false);
    }
  }, [products, loading, hasMore, supermarkets]);

  // Fetch categories for filter
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const productsSnapshot = await getDocs(collection(db, 'Products'));
        const categorySet = new Set();
        const allCategories = [];
        productsSnapshot.docs.forEach(doc => {
          const category = doc.data().category;
          if (category) {
            categorySet.add(category);
            allCategories.push({ id: doc.id, category: category });
          }
        });
        const sortedCategories = Array.from(categorySet).sort();
        setCategories(sortedCategories);
      } catch (error) {
        logger.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  // Initial fetch and when filters change
  useEffect(() => {
    if (Object.keys(supermarkets).length > 0) {
      fetchInitialProducts(supermarkets);
    }
  }, [categoryFilter, supermarkets, fetchInitialProducts]);

  // Infinite scroll implementation
  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 1000) {
        // Check current state values directly to avoid dependency issues
        if (hasMore && !loading) { // Removed loadingMore
          loadMoreProducts();
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadMoreProducts]); // hasMore, loading, loadingMore intentionally excluded to prevent unnecessary re-renders

  // Filter products by search term and delivery availability
  const filteredProducts = products.filter(product => {
    // Search term filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const name = (product.display_name || '').toLowerCase();
      const description = (product.description || '').toLowerCase();
      const category = (product.category || '').toLowerCase();
      const matchesSearch = name.includes(term) || description.includes(term) || category.includes(term);
      if (!matchesSearch) return false;
    }
    
    // Delivery filter
    if (deliveryFilter) {
      const hasDelivery = Object.values(product.supermarketPricing || {}).some(pricing => pricing.deliveryEnabled);
      if (!hasDelivery) return false;
    }
    
    return true;
  });

  // Get cheapest price for a product
  const getCheapestPrice = (supermarketPricing) => {
    const prices = Object.values(supermarketPricing).map(pricing => pricing.sale_price).filter(price => price !== undefined);
    return prices.length > 0 ? Math.min(...prices) : null;
  };

  // Get supermarkets that deliver for a product
  const getDeliveryInfo = (supermarketPricing) => {
    const delivering = Object.entries(supermarketPricing).filter(([_, pricing]) => pricing.deliveryEnabled);
    return delivering.length;
  };

  // Check if a product has deals at any supermarket
  const hasDeals = (productId, supermarketPricing) => {
    return Object.keys(supermarketPricing || {}).some((supermarketId) => {
      const inventoryKey = `${productId}_${supermarketId}`;
      const inventoryData = inventoryDetails[inventoryKey];
      return inventoryData && totalDealsCountForInventory(inventoryData) > 0;
    });
  };

  // Get total number of deals for a product
  const getTotalDeals = (productId, supermarketPricing) => {
    let totalDeals = 0;
    Object.keys(supermarketPricing || {}).forEach((supermarketId) => {
      const inventoryKey = `${productId}_${supermarketId}`;
      const inventoryData = inventoryDetails[inventoryKey];
      if (inventoryData) totalDeals += totalDealsCountForInventory(inventoryData);
    });
    return totalDeals;
  };

  // Toggle expanded state for a product
  const toggleProductExpansion = (productId) => {
    setExpandedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  if (loading && products.length === 0) {
    return (
      <Layout>
        <Container className="mt-4 text-center">
          <p>Loading products...</p>
        </Container>
      </Layout>
    );
  }

  // Flatten all products into rows for space filling
  const productsPerRow = 4;
  const productRows = [];
  for (let i = 0; i < filteredProducts.length; i += productsPerRow) {
    productRows.push(filteredProducts.slice(i, i + productsPerRow));
  }

  const seoTitle = `All Products | ${SITE_NAME}`;
  const seoDescription = 'Browse all grocery products. Compare prices across hardware stores and find the best deals. Shop online with delivery and pickup options.';
  const seoUrl = `${SITE_URL}/products`;
  const seoImage = `${SITE_URL}/logo192.png`;

  return (
    <Layout>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={seoUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={seoUrl} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:image" content={seoImage} />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={seoImage} />
      </Helmet>
      <Container className="mt-4 px-3">
        <Row className="align-items-center mb-1">
          <Col xs="auto">
            <Button variant="outline-secondary" onClick={() => navigate(-1)} className="mb-2">
              &larr; Go Back
            </Button>
          </Col>
          <Col>
            <BreadcrumbNavigation items={breadcrumbItems} />
          </Col>
        </Row>

        {/* Header */}
        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-4">
          <h1 className="mb-3 mb-sm-0">All Products</h1>
          <p className="text-muted mb-0">Compare prices across hardware stores</p>
        </div>

        {/* Dashboard - Best Selling, Trending, Top Categories */}
        <PublicProductsDashboard />

        {/* Filters */}
        <Row className="mb-4">
          <Col xs={12} md={6}>
            <div className="position-relative">
              <Form.Control
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-100"
                style={{ fontSize: '16px' }}
              />
              {searchTerm && (
                <Button
                  variant="outline-secondary"
                  onClick={() => setSearchTerm('')}
                  style={{
                    fontSize: '16px',
                    minWidth: '44px',
                    position: 'absolute',
                    right: '0',
                    top: '0',
                    height: '100%',
                    borderTopLeftRadius: '0',
                    borderBottomLeftRadius: '0',
                    zIndex: 10
                  }}
                >
                  ×
                </Button>
              )}
            </div>
          </Col>
          <Col xs={12} md={3} className="mt-2 mt-md-0">
            <Form.Select
              value={categoryFilter}
              onChange={e => setCategoryFilter(e.target.value)}
              className="w-100"
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </Form.Select>
          </Col>
          <Col xs={12} md={3} className="mt-2 mt-md-0">
            <div className="d-flex align-items-center justify-content-center h-100">
              <Form.Check
                type="switch"
                id="delivery-filter"
                label={
                  <span className="d-flex align-items-center gap-2">
                    <i className="fas fa-truck text-success"></i>
                    <span>Delivery Only</span>
                  </span>
                }
                checked={deliveryFilter}
                onChange={e => setDeliveryFilter(e.target.checked)}
                className="text-nowrap"
              />
            </div>
          </Col>
        </Row>

        {error && <Alert variant="danger">{error}</Alert>}

        {filteredProducts.length === 0 && !loading ? (
          <p>No products found{searchTerm || categoryFilter ? ' matching your criteria.' : '.'}</p>
        ) : (
          <div>
            {productRows.map((row, rowIndex) => (
              <Row key={rowIndex} xs={1} sm={2} md={3} lg={4} className="g-3 mb-4">
                {row.map(product => {
                  const cheapestPrice = getCheapestPrice(product.supermarketPricing);
                  const deliveryCount = getDeliveryInfo(product.supermarketPricing);
                  const availableAt = Object.keys(product.supermarketPricing).length;

                  return (
                    <Col key={product.id}>
                      <Card className="h-100 shadow-sm">
                        {product.media && product.media.length > 0 && product.media[0].url ? (
                          <ProductImage 
                            imagePath={product.media[0].url} 
                            altText={product.display_name} 
                          />
                        ) : (
                          <div className="card-img-top" style={{height: '180px', backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                            <span className="small">No Image</span>
                          </div>
                        )}
                        <Card.Body className="d-flex flex-column">
                          <div className="mb-2 d-flex flex-column gap-1">
                            <Badge bg="secondary" className="text-white small align-self-start">
                              {product.category || 'Uncategorized'}
                            </Badge>
                            {hasDeals(product.id, product.supermarketPricing) && (
                              <Badge bg="danger" className="small align-self-start">
                                <i className="fas fa-tags me-1"></i>
                                {getTotalDeals(product.id, product.supermarketPricing)} Deal{getTotalDeals(product.id, product.supermarketPricing) !== 1 ? 's' : ''}
                              </Badge>
                            )}
                          </div>
                          <Card.Title className="text-break small">{product.display_name || 'Unnamed Product'}</Card.Title>
                          <Card.Text 
                            style={{ 
                              maxHeight: '60px', 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis',
                              fontSize: '0.9rem'
                            }}
                            className="mb-auto"
                          >
                            {product.description || 'No description available.'}
                          </Card.Text>
                          
                          {/* Pricing and Availability Info */}
                          <div className="mt-2">
                            {cheapestPrice ? (
                              <div className="mb-2">
                                <div className="fw-bold text-success">From ${cheapestPrice ? cheapestPrice.toFixed(2) : '0.00'}</div>
                                <small className="text-muted">Available at {availableAt} store{availableAt !== 1 ? 's' : ''}</small>
                              </div>
                            ) : (
                              <div className="mb-2">
                                <div className="text-muted">Currently unavailable</div>
                              </div>
                            )}
                            
                            {/* Delivery Status */}
                            <div className="mb-2 d-flex flex-wrap gap-1">
                              {deliveryCount > 0 ? (
                                <Badge bg="success" className="small">
                                  <i className="fas fa-truck me-1"></i>
                                  {deliveryCount} store{deliveryCount !== 1 ? 's' : ''} deliver{deliveryCount === 1 ? 's' : ''}
                                </Badge>
                              ) : (
                                <Badge bg="secondary" className="small">
                                  <i className="fas fa-store me-1"></i>
                                  In-store pickup only
                                </Badge>
                              )}
                              
                              {/* Show specific delivery stores if only a few */}
                              {deliveryCount > 0 && deliveryCount <= 2 && (
                                Object.entries(product.supermarketPricing)
                                  .filter(([_, pricing]) => pricing.deliveryEnabled)
                                  .map(([supermarketId, pricing]) => (
                                    <Badge key={ supermarketId } bg="outline-success" className="small text-success border border-success">
                                      {pricing.supermarketName} delivers
                                    </Badge>
                                  ))
                              )}
                            </div>

                            {/* Deals Button */}
                            {hasDeals(product.id, product.supermarketPricing) && (
                              <div className="mb-2">
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  className="w-100"
                                  onClick={() => {
                                    handleViewDeals(product);
                                  }}
                                >
                                  <i className="fas fa-tags me-1"></i>
                                  View Deals ({getTotalDeals(product.id, product.supermarketPricing)} available)
                                </Button>
                              </div>
                            )}
                          </div>
                        </Card.Body>
                        <Card.Footer className="bg-transparent border-top-0 p-2">
                          {availableAt > 0 ? (
                            <>
                            <div className="d-flex justify-content-end mb-2">
                              <ShareButton
                                url={getProductShareUrl(product)}
                                title={`${product.display_name} | ${SITE_NAME}`}
                                text={product.description || `Check out ${product.display_name} on ${SITE_NAME}`}
                                variant="outline-secondary"
                                size="sm"
                                showLabel={false}
                              />
                            </div>
                            <div className="d-flex flex-column gap-2">
                              {/* Show first 2 supermarkets when not expanded */}
                              {!expandedProducts.has(product.id) && 
                                Object.entries(product.supermarketPricing).slice(0, 2).map(([supermarketId, pricing]) => (
                                  <Link
                                    key={ supermarketId }
                                    to={`/hardware/${ supermarketId }/products/${getProductShareSlug(product)}`}
                                    className={`btn btn-sm w-100 d-flex justify-content-between align-items-center ${
                                      pricing.deliveryEnabled ? 'btn-outline-success' : 'btn-outline-primary'
                                    }`}
                                    style={{ textDecoration: 'none' }}
                                  >
                                    <div className="d-flex align-items-center gap-2">
                                      <span className="small">{pricing.supermarketName}</span>
                                      {pricing.deliveryEnabled && (
                                        <Badge bg="success" className="small py-0">
                                          <i className="fas fa-truck" style={{ fontSize: '0.7rem' }}></i>
                                        </Badge>
                                      )}
                                    </div>
                                    <div className="d-flex align-items-center gap-1">
                                      <span className="fw-bold">${pricing.sale_price ? pricing.sale_price.toFixed(2) : '0.00'}</span>
                                      {!pricing.deliveryEnabled && (
                                        <small className="text-muted">pickup</small>
                                      )}
                                    </div>
                                  </Link>
                                ))
                              }
                              
                              {/* Show all supermarkets when expanded */}
                              {expandedProducts.has(product.id) && 
                                Object.entries(product.supermarketPricing)
                                  .sort(([,a], [,b]) => (a.sale_price || 0) - (b.sale_price || 0)) // Sort by price ascending
                                  .map(([supermarketId, pricing]) => (
                                    <Link
                                      key={ supermarketId }
                                      to={`/hardware/${ supermarketId }/products/${getProductShareSlug(product)}`}
                                      className={`btn btn-sm w-100 d-flex justify-content-between align-items-center ${
                                        pricing.deliveryEnabled ? 'btn-outline-success' : 'btn-outline-primary'
                                      }`}
                                      style={{ textDecoration: 'none' }}
                                    >
                                      <div className="d-flex align-items-center gap-2">
                                        <span className="small">{pricing.supermarketName}</span>
                                        {pricing.deliveryEnabled && (
                                          <Badge bg="success" className="small py-0">
                                            <i className="fas fa-truck" style={{ fontSize: '0.7rem' }}></i>
                                          </Badge>
                                        )}
                                      </div>
                                      <div className="d-flex align-items-center gap-1">
                                        <span className="fw-bold">${pricing.sale_price ? pricing.sale_price.toFixed(2) : '0.00'}</span>
                                        {!pricing.deliveryEnabled && (
                                          <small className="text-muted">pickup</small>
                                        )}
                                      </div>
                                    </Link>
                                  ))
                              }
                              
                              {/* Toggle button */}
                              {availableAt > 2 && (
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="p-0 text-center"
                                  onClick={() => toggleProductExpansion(product.id)}
                                  style={{ fontSize: '0.85rem' }}
                                >
                                  {expandedProducts.has(product.id) ? (
                                    <>
                                      <i className="fas fa-chevron-up me-1"></i>
                                      Show Less
                                    </>
                                  ) : (
                                    <>
                                      <i className="fas fa-chevron-down me-1"></i>
                                      View All Prices ({availableAt} stores)
                                    </>
                                  )}
                                </Button>
                              )}
                            </div>
                            </>
                          ) : (
                            <Button variant="outline-secondary" className="w-100" disabled>
                              Currently Unavailable
                            </Button>
                          )}
                        </Card.Footer>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            ))}
          </div>
        )}

        {/* Loading indicator for infinite scroll */}
        {loading && ( // Removed loadingMore
          <Row className="justify-content-center mt-4">
            <Col xs="auto">
              <div className="text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted">Loading more products...</p>
              </div>
            </Col>
          </Row>
        )}
        
        {/* End of products indicator */}
        {!hasMore && filteredProducts.length > 0 && (
          <Row className="justify-content-center mt-4">
            <Col xs="auto">
              <p className="text-muted text-center">No more products to load</p>
            </Col>
          </Row>
        )}

        {/* Add spacing before footer */}
        <div className="mb-5"></div>

        {/* Deals Modal */}
        <Modal show={showDealsModal} onHide={handleCloseDealsModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="fas fa-tags text-danger me-2"></i>
              Available Deals - {selectedProductName}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3">
              <p className="text-muted mb-2">
                <strong>Available Deals:</strong> Compare bulk pricing across all hardware stores
              </p>
              <p className="small text-muted">
                Save money by purchasing in bulk! The more you buy, the more you save. Each deal shows the supermarket and delivery options.
              </p>
            </div>
            
            <ListGroup>
              {selectedProductDeals.map((deal, index) => {
                const regularTotal = deal.regularPrice * deal.quantity;
                const savings = regularTotal - deal.price;
                const savingsPercentage = regularTotal > 0 ? ((savings / regularTotal) * 100).toFixed(1) : '0.0';
                
                return (
                  <ListGroup.Item key={index} className="d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <div className="fw-bold">
                            Buy {deal.quantity} for ${deal.price ? deal.price.toFixed(2) : '0.00'}
                          </div>
                          <div className="text-muted small">
                            Regular price: ${regularTotal ? regularTotal.toFixed(2) : '0.00'} (${deal.regularPrice ? deal.regularPrice.toFixed(2) : '0.00'} each)
                          </div>
                        </div>
                        <div className="text-end">
                          <div className="text-success fw-bold">
                            Save ${savings ? savings.toFixed(2) : '0.00'}
                          </div>
                          <div className="text-success small">
                            ({savingsPercentage}% off)
                          </div>
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        {deal.productTypeName && (
                          <Badge bg="info" className="small">
                            {deal.productTypeName}
                          </Badge>
                        )}
                        <Badge bg="primary" className="small">
                          <i className="fas fa-store me-1"></i>
                          {deal.supermarketName}
                        </Badge>
                        {deal.deliveryEnabled ? (
                          <Badge bg="success" className="small">
                            <i className="fas fa-truck me-1"></i>
                            Delivery Available
                          </Badge>
                        ) : (
                          <Badge bg="secondary" className="small">
                            <i className="fas fa-walking me-1"></i>
                            In-Store Pickup
                          </Badge>
                        )}
                      </div>
                    </div>
                  </ListGroup.Item>
                );
              })}
            </ListGroup>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseDealsModal}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </Layout>
  );
}
